import React, { useEffect, useState, createContext, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useUserProfile from "../hooks/useUserProfile";
import { getAuthEntry } from "../utils/authFlow";

// Create context for user profile data
const UserProfileContext = createContext();

// TEST
/**
 * Custom hook to access user profile context
 */
export const useUserProfileContext = () => {
  const context = useContext(UserProfileContext);
  if (!context) {
    throw new Error(
      "useUserProfileContext must be used within a ProtectedRoute",
    );
  }
  return context;
};

const ProtectedRoute = ({ children, requireCompleteAccount = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  // Use the user profile hook to fetch user data when token is available
  const {
    profile,
    loading: profileLoading,
    error: profileError,
    refetchProfile,
    isAuthenticated,
  } = useUserProfile();

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem("authToken");
      const eventCode = localStorage.getItem("eventCode");
      const completeAccountFlag =
        localStorage.getItem("isCompleteAccount") === "true";

      if (!token || (!profileLoading && !isAuthenticated)) {
        setHasAccess(false);
        // Not authenticated, redirect to join page
        // Check if current path is /upload-event-images/[EVENT_TOKEN] and redirect accordingly
        const currentPath = location.pathname;
        if (/^\/events\/(detail|album)\//.test(currentPath)) {
          const eventCodeFromPath = decodeURIComponent(currentPath.split('/')[3]);
          const destination = currentPath.split('/').slice(1, 3).join('/');
          navigate(getAuthEntry(eventCodeFromPath, destination), { replace: true });
        } else if (currentPath.startsWith("/upload-event-images/")) {
          // Extract the path after /upload-event-images/ and include it in the redirect
          const qrFlowPath = currentPath.replace("/upload-event-images/", "");
          navigate(
            `/events/upload-join/${qrFlowPath}?redirect-to=upload-event-images`,
          );
        } else {
          navigate(eventCode && currentPath !== "/profile" ? `/events/join/${encodeURIComponent(eventCode)}` : "/login");
        }
        return;
      }

      // Check if complete account is required
      if (requireCompleteAccount && !completeAccountFlag) {
        // User has not completed account, redirect to complete account page
        if (location.pathname.startsWith("/upload-event-images/")) {
          const uploadCode = location.pathname.slice("/upload-event-images/".length);
          localStorage.setItem("eventCode", decodeURIComponent(uploadCode));
          navigate("/auth/complete-account?redirect-to=upload-event-images");
        } else {
          navigate("/auth/complete-account");
        }
        return;
      }

      setHasAccess(true);
      setLoading(false);
    };

    checkAuth();
  }, [navigate, requireCompleteAccount, location.pathname, isAuthenticated, profileLoading]);

  // // Show loading state while checking auth or fetching profile
  // if (loading || profileLoading) {
  //   return (
  //     <div className="loading-container">
  //       <div className="loading-spinner">
  //         <div className="spinner"></div>
  //         <div>Loading...</div>
  //       </div>
  //     </div>
  //   );
  // }

  return hasAccess && isAuthenticated ? (
    <UserProfileContext.Provider
      value={{
        profile,
        loading: loading || profileLoading,
        error: profileError,
        refetchProfile,
        isAuthenticated,
      }}
    >
      {children}
    </UserProfileContext.Provider>
  ) : null;
};

export default ProtectedRoute;
